import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkout } from '../context/useWorkout';
import { soundManager } from '../utils/SoundManager';
import heroTrainersData from '../data/trainers/HeroTrainersData';

import './HeroTrainers.css';

const HeroTrainers = () => {
    const { trainerId } = useParams();
    const navigate = useNavigate();
    const { actions } = useWorkout();
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);

    useEffect(() => {
        console.log('HeroTrainers mounted, trainerId:', trainerId);

        if (trainerId) {
            // Find the hero trainer by ID
            const trainer = heroTrainersData[trainerId];
            console.log('Looking for hero trainer:', trainerId);
            console.log('Found trainer:', trainer);

            if (trainer) {
                setSelectedTrainer(trainer);
            } else {
                console.log('Hero trainer not found:', trainerId, 'redirecting to /hero-trainers');
                navigate('/hero-trainers');
            }
        } else {
            // Show all hero trainers
            setSelectedTrainer(null);
        }
    }, [trainerId, navigate]);

    // Handle trainer selection from the list
    const handleTrainerSelect = (trainer) => {
        console.log('Hero trainer selected:', trainer);
        soundManager.playSound('gameIntroMode2', 0.7);
        navigate(`/hero-trainers/${trainer.id}`);
    };

    // Handle program selection - FIXED TO GO TO TRAINER PAGE WITH INTENSITY SELECTION
    const handleProgramSelect = (program) => {
        console.log('Hero program selected:', program);
        soundManager.playSound('gameIntroMode2', 0.7);

        // Set the selected program and trainer
        setSelectedProgram(program);
        actions.setMuscleGroups(program.pattern);

        // ✅ FIXED: Navigate to TrainerPage with intensity selection
        // The TrainerPage will handle hero trainers and show intensity modal immediately
        navigate(`/trainer/${selectedTrainer.id}/${program.id}/intensity-selection`, {
            state: {
                trainerId: selectedTrainer.id,
                programId: program.id,
                trainerData: selectedTrainer,
                programData: program,
                workoutType: 'hero',
                isHeroTrainer: true,
                workoutCode: program.pattern
            }
        });
    };

    // Handle back navigation
    const handleBack = () => {
        soundManager.playButtonSound();
        if (selectedTrainer) {
            navigate('/hero-trainers');
        } else {
            navigate('/train-categories');
        }
    };

    // If showing individual trainer
    if (selectedTrainer) {
        return (
            <div className="hero-trainers-container" style={{ '--hero-primary-color': selectedTrainer.color, '--hero-secondary-color': selectedTrainer.secondaryColor }}>
                <div className="hero-animated-background"></div>

                <header className="hero-trainers-header">
                    <button className="back-to-categories-button" onClick={handleBack}>
                        ← Back to Hero Trainers
                    </button>
                    <div className="header-content">
                        <h1>{selectedTrainer.name}</h1>
                        <p>{selectedTrainer.specialization}</p>
                        <div className="category-stats">
                            <span className="stat-badge">Experience: {selectedTrainer.experience}</span>
                            <span className="stat-badge">Programs: {selectedTrainer.programs.length}</span>
                            <span className="stat-badge">Category: Hero</span>
                        </div>
                    </div>
                </header>

                <div className="trainer-profile-section">
                    <div className="hero-trainer-image-section">
                        <div
                            className="hero-trainer-image"
                            style={{
                                backgroundImage: `url(${selectedTrainer.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }}
                        >
                            <div className="hero-glow-effect"></div>
                            <div className="experience-badge hero-badge">
                                {selectedTrainer.experience}
                            </div>
                        </div>
                    </div>

                    <div className="hero-trainer-info-section">
                        <div className="hero-trainer-description">
                            <p>{selectedTrainer.intro}</p>
                        </div>
                    </div>
                </div>

                <div className="programs-section">
                    <h2 style={{ textAlign: 'center', color: selectedTrainer.color, marginBottom: '30px' }}>Training Programs</h2>
                    <div className="hero-trainers-grid">
                        {selectedTrainer.programs.map((program) => (
                            <div
                                key={program.id}
                                className="hero-trainer-card"
                                onClick={() => handleProgramSelect(program)}
                                style={{
                                    '--hero-primary-color': selectedTrainer.color,
                                    '--hero-secondary-color': selectedTrainer.secondaryColor
                                }}
                            >
                                <div className="hero-trainer-info-section">
                                    <h3 className="hero-trainer-name">{program.name}</h3>
                                    <h4 className="hero-trainer-specialization">{program.focus}</h4>
                                    <p className="hero-trainer-description">{program.description}</p>

                                    <div className="hero-trainer-details">
                                        <div className="detail-item hero-detail">
                                            <span className="detail-icon">🎯</span>
                                            <span className="detail-text">Reps: {program.reps}</span>
                                        </div>
                                        <div className="detail-item hero-detail">
                                            <span className="detail-icon">📋</span>
                                            <span className="detail-text">Sets: {program.sets}</span>
                                        </div>
                                        <div className="detail-item hero-detail">
                                            <span className="detail-icon">⚡</span>
                                            <span className="detail-text">Pattern: {program.pattern}</span>
                                        </div>
                                    </div>

                                    <div className="hero-trainer-action">
                                        <button className="select-hero-trainer-button">
                                            Start Training
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <footer className="hero-trainers-footer">
                    <div className="hero-info">
                        <h3>Select a training program to begin your hero journey</h3>
                    </div>
                </footer>
            </div>
        );
    }

    // Show all hero trainers
    return (
        <div className="hero-trainers-container">
            <div className="hero-animated-background"></div>

            <header className="hero-trainers-header">
                <button className="back-to-categories-button" onClick={handleBack}>
                    ← Back to Train Categories
                </button>
                <div className="header-content">
                    <h1>Hero Trainers</h1>
                    <p>Train with legendary superheroes and unlock your potential</p>
                    <div className="category-stats">
                        <span className="stat-badge">6 Hero Trainers</span>
                        <span className="stat-badge">30+ Programs</span>
                        <span className="stat-badge">All Power Levels</span>
                    </div>
                </div>
            </header>

            <div className="hero-trainers-grid">
                {Object.values(heroTrainersData).map((trainer) => (
                    <div
                        key={trainer.id}
                        className="hero-trainer-card"
                        onClick={() => handleTrainerSelect(trainer)}
                        style={{
                            '--hero-primary-color': trainer.color,
                            '--hero-secondary-color': trainer.secondaryColor
                        }}
                    >
                        <div className="hero-trainer-image-section">
                            <div
                                className="hero-trainer-image"
                                style={{
                                    backgroundImage: `url(${trainer.image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                <div className="hero-glow-effect"></div>
                                <div className="experience-badge hero-badge">
                                    {trainer.experience}
                                </div>
                            </div>
                        </div>

                        <div className="hero-trainer-info-section">
                            <h3 className="hero-trainer-name">{trainer.name}</h3>
                            <h4 className="hero-trainer-specialization">{trainer.specialization}</h4>
                            <p className="hero-trainer-description">{trainer.intro.substring(0, 120)}...</p>

                            <div className="hero-trainer-details">
                                <div className="detail-item hero-detail">
                                    <span className="detail-icon">⚡</span>
                                    <span className="detail-text">{trainer.experience}</span>
                                </div>
                                <div className="detail-item hero-detail">
                                    <span className="detail-icon">📋</span>
                                    <span className="detail-text">{trainer.programs.length} Programs</span>
                                </div>
                            </div>

                            <div className="hero-trainer-action">
                                <button className="select-hero-trainer-button">
                                    View Programs
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <footer className="hero-trainers-footer">
                <div className="hero-info">
                    <h3>🦸‍♂️ Hero Training Benefits</h3>
                    <div className="hero-benefits-grid">
                        <div className="hero-benefit-item">
                            <span className="benefit-icon">⚡</span>
                            <div>
                                <strong>Superhero Conditioning</strong>
                                <p>Train like your favorite heroes with specialized programs</p>
                            </div>
                        </div>
                        <div className="hero-benefit-item">
                            <span className="benefit-icon">🎯</span>
                            <div>
                                <strong>Character-Based Training</strong>
                                <p>Each hero offers unique training philosophies and methods</p>
                            </div>
                        </div>
                        <div className="hero-benefit-item">
                            <span className="benefit-icon">🔥</span>
                            <div>
                                <strong>Maximum Effort</strong>
                                <p>Push your limits with heroic intensity and determination</p>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HeroTrainers;